import React, { useCallback, useEffect, useRef, useState } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus, FlowArrow, Play, Pause, DotsThree,
  PencilSimple, Copy, Trash,
  CircleNotch, Flask, FileText, Sparkle,
  CaretLeft, CaretRight, ArrowsClockwise,
} from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useWorkflowStore } from '@/stores/workflowStore'
import { usePermission } from '@/hooks/usePermission'
import { useTrialStatus } from '@/hooks/useTrialStatus'
import { Select } from '@/components/ui/Select'
import { formatDistanceToNowStrict } from 'date-fns'
import type { WorkflowListItem } from '@/types/workflow'
import { WorkflowPlaygroundModal } from './components/playground/WorkflowPlaygroundModal'
import AIWorkflowWizard from './AIWorkflowWizard'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 10

const statusDotColor: Record<string, string> = {
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  draft: 'bg-[#9ca3af] dark:bg-[#8a8f98]',
  stopped: 'bg-red-500',
  // An Owner/Admin/Manager declined a Rep's activation request -- same red
  // as stopped, distinct from the muted "never activated" draft dot.
  rejected: 'bg-red-500',
}

// Right-aligned KPI tile: caption above, tabular value below, separated by
// hairline dividers. Muted state used when there is no data yet (sent = 0)
// so a fresh workflow doesn't shout zeros.
const MetricCell: React.FC<{ label: string; value: string; muted?: boolean }> = ({ label, value, muted }) => (
  <div className="flex flex-col items-end justify-center px-3 first:pl-0 last:pr-0 border-r last:border-r-0 border-border/40 dark:border-white/[0.06] w-[76px]">
    <span
      className="text-muted-foreground/70 dark:text-[#62666d] uppercase"
      style={{ fontSize: '9.5px', fontWeight: 590, letterSpacing: '0.09em', lineHeight: 1 }}
    >
      {label}
    </span>
    <span
      className={cn(
        'tabular-nums mt-1.5',
        muted ? 'text-muted-foreground/50 dark:text-[#62666d]' : 'text-foreground dark:text-[#f7f8f8]',
      )}
      style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.18px', lineHeight: 1 }}
    >
      {value}
    </span>
  </div>
)

const statConfig = [
  {
    label: 'Total',
    key: 'total' as const,
    icon: FlowArrow,
    iconColor: 'text-[#5e6ad2] dark:text-[#a5b4fc]',
    bgColor: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]',
    // Symmetric glyph — no optical nudge.
    iconNudge: '',
  },
  {
    label: 'Active',
    key: 'active' as const,
    icon: Play,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500/10 dark:bg-emerald-500/[0.10]',
    // Triangle apex sits right; centroid is ~33% from left. Shift right ~1px
    // so visual center aligns with chip center.
    iconNudge: 'translate-x-[1px]',
  },
  {
    label: 'Paused',
    key: 'paused' as const,
    icon: Pause,
    iconColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10 dark:bg-amber-500/[0.10]',
    iconNudge: '',
  },
  {
    label: 'Draft',
    key: 'draft' as const,
    icon: FileText,
    iconColor: 'text-muted-foreground dark:text-[#8a8f98]',
    bgColor: 'bg-muted/60 dark:bg-white/[0.04]',
    iconNudge: '',
  },
]

const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    workflows, stats, isLoading,
    fetchWorkflows, fetchStats,
    deleteWorkflow, duplicateWorkflow,
    activateWorkflow, deactivateWorkflow,
    members, createdByUserId, fetchMembers, setCreatedByFilter,
  } = useWorkflowStore()
  const { allowed: canActivate } = usePermission('workflows', 'activate')
  const { isTrial, limits: trialLimits } = useTrialStatus()
  // Analyst holds workflows.manage = 'view' (spec 4-H: View), so every write
  // control on this page -- Create, Duplicate, Delete -- is hidden for them
  // instead of navigating into a builder whose Save can only 403 (AB#22339).
  const { canWrite: canWriteWorkflows, scope: workflowScope } = usePermission('workflows', 'manage')
  // "Created by" team filter. Account-wide scopes (Owner/Admin/Manager "all",
  // Analyst "view") review other people's work — the same roles that approve
  // activations. A Rep's level is "draft" (own drafts), so the dropdown is
  // hidden for them and the API rejects the param regardless.
  const canFilterByMember =
    workflowScope !== null && workflowScope !== 'own' && workflowScope !== 'draft'
  const [openMenu, setOpenMenu] = useState<number | null>(null)
  const [menuCoords, setMenuCoords] = useState<{ top: number; right: number } | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // The URL is the source of truth for pagination, so reload and browser Back
  // both land on the page the user was looking at. 1-based in the URL, 0-based
  // internally. Missing / non-numeric / zero / negative all resolve to page 0.
  const pageParam = Number(searchParams.get('page'))
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? Math.floor(pageParam) - 1 : 0

  // Page 0 deletes the param instead of writing `?page=1` so the default URL
  // stays clean and the two spellings of "first page" can't diverge.
  const goToPage = useCallback((next: number, replace = false) => {
    const target = Math.max(0, next)
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (target === 0) params.delete('page')
      else params.set('page', String(target + 1))
      return params
    }, { replace })
  }, [setSearchParams])

  const total = stats.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = page * PAGE_SIZE + workflows.length

  // Focus returns to the ⋯ trigger that opened the menu when Escape closes it.
  const menuTriggerRef = useRef<HTMLElement | null>(null)
  // Roving focus reads the items off the DOM rather than tracking an index per
  // item in state.
  const menuRef = useRef<HTMLDivElement | null>(null)
  // True when the trigger was activated by keyboard, in which case the menu
  // takes focus on its first item; a mouse open leaves focus where it was.
  const [autoFocusMenu, setAutoFocusMenu] = useState(false)

  const closeMenu = useCallback(() => {
    setOpenMenu(null)
    setMenuCoords(null)
    setAutoFocusMenu(false)
  }, [])

  const closeMenuAndRestoreFocus = useCallback(() => {
    const trigger = menuTriggerRef.current
    closeMenu()
    trigger?.focus()
  }, [closeMenu])

  const toggleMenu = (workflowId: number, target: HTMLElement, viaKeyboard: boolean) => {
    if (openMenu === workflowId) {
      closeMenu()
      return
    }
    const rect = target.getBoundingClientRect()
    menuTriggerRef.current = target
    setMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
    setAutoFocusMenu(viaKeyboard)
    setOpenMenu(workflowId)
  }

  useEffect(() => {
    if (openMenu === null) return
    const onDismiss = () => closeMenu()
    window.addEventListener('scroll', onDismiss, true)
    window.addEventListener('resize', onDismiss)
    return () => {
      window.removeEventListener('scroll', onDismiss, true)
      window.removeEventListener('resize', onDismiss)
    }
  }, [openMenu, closeMenu])

  // Menu keyboard model (§19): Escape closes and hands focus back to the
  // trigger; Arrow/Home/End rove between items. Deliberately no Enter/Space
  // handler here — the focused menuitem already activates on its own (§57).
  useEffect(() => {
    if (openMenu === null) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeMenuAndRestoreFocus()
        return
      }
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      )
      if (items.length === 0) return
      const current = items.indexOf(document.activeElement as HTMLElement)
      let next: number
      if (e.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % items.length
      else if (e.key === 'ArrowUp') next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length
      else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = items.length - 1
      else return
      e.preventDefault()
      items[next].focus()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [openMenu, closeMenuAndRestoreFocus])

  useEffect(() => {
    if (openMenu === null || !autoFocusMenu) return
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
  }, [openMenu, autoFocusMenu])

  const [playgroundId, setPlaygroundId] = useState<number | null>(null)
  const [playgroundName, setPlaygroundName] = useState('')
  const [showAIWizard, setShowAIWizard] = useState(false)
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()

  useEffect(() => {
    fetchStats()
    if (canFilterByMember) fetchMembers()
  }, [canFilterByMember])

  useEffect(() => {
    fetchWorkflows(page * PAGE_SIZE, PAGE_SIZE)
  }, [page])

  // Changing the filter resets to page 1 and refetches both the rows and the
  // tiles, so the counts never describe a different set than the list.
  const handleCreatedByChange = (value: string) => {
    setCreatedByFilter(value === 'all' ? null : Number(value))
    goToPage(0)
    fetchWorkflows(0, PAGE_SIZE)
    fetchStats()
  }

  // A hand-typed or stale `?page=` past the end would otherwise strand the user
  // on a permanently empty list. Gated on `total > 0` because totalPages floors
  // at 1, so before stats resolve every deep link would look out of range.
  useEffect(() => {
    if (total > 0 && page >= totalPages) {
      goToPage(totalPages - 1, true)
    }
  }, [page, total, totalPages, goToPage])

  const handleDelete = async (id: number) => {
    setIsDeleting(true)
    try {
      await deleteWorkflow(id)
      setDeleteConfirmId(null)
      // If the last row on a non-first page was removed, step back a page
      // (which refetches via the effect); otherwise reload the current page.
      // `replace` because this is a correction, not somewhere the user chose to
      // go — Back should not walk them onto the page they just emptied.
      if (workflows.length === 1 && page > 0) {
        goToPage(page - 1, true)
      } else {
        fetchWorkflows(page * PAGE_SIZE, PAGE_SIZE)
      }
      fetchStats()
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDuplicate = async (id: number) => {
    await duplicateWorkflow(id)
    fetchWorkflows(page * PAGE_SIZE, PAGE_SIZE)
    fetchStats()
    closeMenu()
  }

  const handleToggleStatus = async (workflow: WorkflowListItem) => {
    if (workflow.status === 'active') {
      await deactivateWorkflow(workflow.id)
    } else {
      await activateWorkflow(workflow.id)
    }
    fetchWorkflows(page * PAGE_SIZE, PAGE_SIZE)
    fetchStats()
    closeMenu()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1
            className="text-foreground dark:text-[#f7f8f8] truncate"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            Email Workflows
          </h1>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          >
            Create and manage automated email sequences with a visual builder.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            icon={<Sparkle className="h-4 w-4" />}
            onClick={() => setShowAIWizard(true)}
            disabled={planLapsed}
            title={lapsedTitle}
            className="dark:border-white/[0.10] dark:bg-transparent dark:text-[#d0d6e0] dark:hover:bg-white/[0.04]"
          >
            AI Generate
          </Button>
          {/* A Rep's own workflows are "draft" scope: activating one is a
              request, not an action, so an Owner/Admin/Manager has to
              approve it elsewhere before it goes live. This list is a Rep's
              only way to see the status flip from "Pending approval" to
              "Active" without leaving the page and coming back. Nobody else
              needs it -- everyone else can activate outright, and sees the
              result of their own click immediately. */}
          {workflowScope === 'draft' && (
            <Button
              variant="outline"
              size="sm"
              loading={isLoading}
              onClick={() => fetchWorkflows(page * PAGE_SIZE, PAGE_SIZE)}
              icon={<ArrowsClockwise className="h-4 w-4" weight="regular" />}
              aria-label="Refresh workflow list"
            >
              Refresh
            </Button>
          )}
          {canWriteWorkflows && (
            <Button
              variant="default"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/workflows/builder')}
            >
              Create workflow
            </Button>
          )}
        </div>
      </div>

      {/* Free-trial cap, proactive — only ever shown to a trial account. */}
      {isTrial && trialLimits && (
        <p
          className="text-muted-foreground dark:text-[#8a8f98]"
          style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
        >
          {stats.total} of {trialLimits.workflows} workflows used (free trial)
        </p>
      )}

      {/* Hero stats block — title left, decorative indigo cloud right, stat pills bottom */}
      <section
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/50 p-5 sm:p-6',
          'bg-gradient-to-br from-white via-[#f6f7ff] to-[#eef0ff]',
          'dark:border-white/[0.08] dark:from-[#0f1011] dark:via-[#131421] dark:to-[#1a1b2e]',
        )}
      >
        {/* Decorative cloud / halftone — the approved Workflows motif per §51
            (gradient mesh + halftone dots + cloud-shape clip), theme-aware via
            currentColor.

            The band is a FIXED 100px pinned to the top, not `inset-y-0`. With a
            full-height wrapper, `slice` scales the drawing by the panel height,
            so the taller mobile layout (stat pills stack to grid-cols-2) blew
            the cloud straight down into the pill row — the §51 violation this
            fixes. Locking the wrapper to 100px and the viewBox to 100 units
            makes the vertical scale exactly 1 at every breakpoint (width scale
            can only ever be ≤ 1, so `slice` picks the height), and the geometry
            below is authored so nothing is drawn past y=100. `overflow-hidden`
            clips the horizontal slice, matching CampaignsPage's wrapper. */}
        <div
          className="pointer-events-none absolute top-0 right-0 h-[100px] w-2/3 max-w-[560px] overflow-hidden text-[#7170ff] dark:text-[#7170ff]"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 560 100"
            preserveAspectRatio="xMaxYMid slice"
            className="absolute inset-0 h-full w-full opacity-80 dark:opacity-60"
          >
            <defs>
              <radialGradient id="wf-glow-1" cx="70%" cy="-10%" r="55%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="wf-glow-2" cx="40%" cy="18%" r="40%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.12" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
              <pattern id="wf-dots" x="0" y="0" width="7" height="7" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.9" fill="currentColor" fillOpacity="0.35" />
              </pattern>
              <pattern id="wf-lines" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.28" />
              </pattern>
              <clipPath id="wf-cloud">
                {/* Same organic silhouette, re-anchored above the top edge: the
                    lobes' bulk sits at negative y, so only the lower crest
                    (y ≈ 22 → 84) enters the band. */}
                <path d="M -20 -74 C -62 -22 -28 32 30 26 C 52 66 122 74 156 42 C 190 80 268 84 306 46 C 344 70 414 62 432 22 C 496 18 522 -34 476 -76 Z" />
              </clipPath>
            </defs>

            {/* Soft glows — both centres now sit in the upper band */}
            <rect width="100%" height="100%" fill="url(#wf-glow-1)" />
            <rect width="100%" height="100%" fill="url(#wf-glow-2)" />

            {/* Clipped halftone cloud — vertical-line shaded, dots concentrated
                on the visible crest. Both rects stop at y=100 (the band floor). */}
            <g clipPath="url(#wf-cloud)">
              <rect x="-40" y="-80" width="620" height="180" fill="url(#wf-lines)" />
              <rect x="-40" y="0" width="620" height="100" fill="url(#wf-dots)" opacity="0.7" />
            </g>

            {/* Outline whisper on cloud */}
            <path
              d="M -20 -74 C -62 -22 -28 32 30 26 C 52 66 122 74 156 42 C 190 80 268 84 306 46 C 344 70 414 62 432 22 C 496 18 522 -34 476 -76 Z"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* Title */}
        <div className="relative z-10 max-w-md">
          <h2
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
          >
            Workflow performance
          </h2>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
          >
            Overview of automated email sequences across your workspace.
          </p>
        </div>

        {/* Stat pills — float over the decoration */}
        <div className="relative z-10 mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {statConfig.map(stat => {
            const value = stats[stat.key] ?? 0
            return (
              <div
                key={stat.label}
                className={cn(
                  'rounded-xl border bg-white px-4 py-5 transition-colors duration-150',
                  'border-border/40 shadow-[0_1px_2px_rgba(15,16,17,0.04),0_4px_12px_rgba(15,16,17,0.04)]',
                  'dark:bg-[#191a1b]/85 dark:backdrop-blur-sm dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.25)]',
                )}
              >
                <p
                  className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
                >
                  {value}
                </p>
                <div className="mt-2.5 flex items-center gap-2">
                  <div
                    className={cn(
                      'h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0',
                      stat.bgColor,
                    )}
                    aria-hidden="true"
                  >
                    <stat.icon className={cn('h-3.5 w-3.5', stat.iconColor, stat.iconNudge)} />
                  </div>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {stat.label}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* "Created by" team filter — hidden for own-scoped roles (Rep) and for
          solo accounts where there is no one else to filter by. */}
      {canFilterByMember && members.length > 1 && (
        <div className="flex items-center gap-2">
          <span
            className="text-muted-foreground dark:text-[#8a8f98] flex-shrink-0"
            style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
          >
            Created by
          </span>
          <Select
            ariaLabel="Filter workflows by team member"
            className="w-56"
            value={createdByUserId === null ? 'all' : String(createdByUserId)}
            onChange={handleCreatedByChange}
            options={[
              { value: 'all', label: 'All members' },
              ...members.map((m) => ({
                value: String(m.id),
                // A former member's workflows stay with the team that owns
                // them, so they stay selectable — greyed to show they have
                // left, never removed, or the counts would stop adding up.
                label: (
                  <span className={m.is_member ? undefined : 'text-muted-foreground dark:text-[#8a8f98]'}>
                    {m.name} ({m.workflow_count})
                  </span>
                ),
                description: m.is_member ? m.email : `${m.email} — former member`,
              })),
            ]}
          />
        </div>
      )}

      {/* Workflow List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch className="w-6 h-6 animate-spin text-muted-foreground dark:text-[#8a8f98]" />
        </div>
      ) : workflows.length === 0 ? (
        <Card
          variant="default"
          className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
        >
          <CardContent className="p-14 text-center">
            <div className="max-w-md mx-auto">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <FlowArrow className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8] mb-2"
                style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
              >
                {canWriteWorkflows ? 'Create your first workflow' : 'No workflows yet'}
              </h3>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                {canWriteWorkflows
                  ? 'Build automated email sequences with the visual builder. Set up triggers, emails, delays, and conditions.'
                  : 'Workflows your team builds will show up here.'}
              </p>
              {canWriteWorkflows && (
                <Button
                  variant="default"
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate('/workflows/builder')}
                >
                  Create workflow
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card
          variant="default"
          padding="none"
          className="rounded-lg border-border/50 overflow-hidden dark:bg-white/[0.02] dark:border-white/[0.08]"
        >
          <CardContent className="p-0">
            {workflows.map((workflow, index) => {
              const sent = workflow.total_sent || 0
              const opened = workflow.total_opened || 0
              const clicked = workflow.total_clicked || 0
              const openRate = sent ? Math.round((opened / sent) * 100) : 0
              const clickRate = sent ? Math.round((clicked / sent) * 100) : 0
              const statusKey = (workflow.status || 'draft').toLowerCase()
              // The row is a pointer affordance only: it keeps `onClick` but
              // carries no role, tabIndex or key handler. §57 — the row held
              // `role="button"` around three real buttons, and its `onKeyDown`
              // fired on Enter/Space from those buttons (the actions container
              // stops click, not keydown), so activating Playground also
              // navigated. The keyboard path now lives on the name button below.
              return (
                <div
                  key={workflow.id}
                  onClick={() => navigate(`/workflows/builder/${workflow.id}`)}
                  className={cn(
                    'group flex items-center gap-5 px-5 py-4 transition-colors duration-150 cursor-pointer',
                    'hover:bg-muted/40 dark:hover:bg-white/[0.025]',
                    index !== workflows.length - 1 && 'border-b border-border/50 dark:border-white/[0.06]',
                  )}
                >
                  {/* Workflow chip — same icon for every row; status encoded
                      via a colored dot in the top-right corner of the chip.
                      `role="img"` so the label is actually announced: an
                      aria-label on a bare <div> is dropped by screen readers. */}
                  <div
                    className="relative flex-shrink-0"
                    role="img"
                    aria-label={`Status: ${statusKey}`}
                    title={statusKey}
                  >
                    <div className="h-9 w-9 rounded-md bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center">
                      <FlowArrow className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                    </div>
                    <span
                      className={cn(
                        'absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background dark:ring-[#0f1011]',
                        statusDotColor[statusKey] || 'bg-muted-foreground/40',
                      )}
                      aria-hidden="true"
                    />
                  </div>

                  {/* Title + description — the name is the row's one real
                      control: focusable, Enter/Space-operable, ring on focus
                      (§13 permits it on buttons). `block max-w-full truncate`
                      keeps §41 truncation working now that the text is inside a
                      shrink-to-fit button. stopPropagation so a click isn't also
                      handled by the row, which would push the same route twice. */}
                  <div className="flex-1 min-w-0">
                    <h3 className="min-w-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/workflows/builder/${workflow.id}`) }}
                        className={cn(
                          'block max-w-full truncate text-left rounded-sm',
                          'text-foreground dark:text-[#f7f8f8] group-hover:text-[#5e6ad2] dark:group-hover:text-[#a5b4fc] transition-colors duration-150',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        )}
                        style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.17px', lineHeight: 1.35 }}
                      >
                        {workflow.name}
                      </button>
                    </h3>
                    {workflow.description && (
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] truncate mt-0.5"
                        style={{ fontSize: '12.5px', letterSpacing: '-0.13px', lineHeight: 1.45 }}
                      >
                        {workflow.description}
                      </p>
                    )}
                  </div>

                  {/* KPI strip — hidden on small screens to keep mobile clean */}
                  <div className="hidden lg:flex items-stretch flex-shrink-0">
                    <MetricCell label="Nodes" value={String(workflow.node_count)} />
                    <MetricCell label="Sent" value={String(sent)} muted={!sent} />
                    <MetricCell label="Open" value={`${openRate}%`} muted={!sent} />
                    <MetricCell label="Click" value={`${clickRate}%`} muted={!sent} />
                  </div>

                  {/* Updated — quiet caption. Fixed-width and always rendered
                      (empty when a row has no timestamp), because this column
                      is what sets where the KPI strip ends: a variable-width
                      caption pushed "about 1 month ago" rows' Nodes/Sent/Open/
                      Click cells left of the rows saying "25 days ago", so no
                      metric lined up with the one above it. `Strict` also drops
                      date-fns' "about"/"less than a minute" prefixes, which were
                      the widest strings in the column. */}
                  <span
                    className="hidden md:block w-24 flex-shrink-0 text-right text-muted-foreground/60 dark:text-[#62666d] tabular-nums whitespace-nowrap truncate"
                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
                    title={workflow.created_at ? new Date(workflow.created_at).toLocaleString() : undefined}
                  >
                    {workflow.created_at
                      ? formatDistanceToNowStrict(new Date(workflow.created_at), { addSuffix: true })
                      : ''}
                  </span>

                  {/* Actions — always visible per design feedback */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setPlaygroundId(workflow.id); setPlaygroundName(workflow.name) }}
                      icon={<Flask className="w-3.5 h-3.5" />}
                      className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                    >
                      Playground
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/workflows/builder/${workflow.id}`)}
                      icon={<PencilSimple className="w-3.5 h-3.5" />}
                      className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="More options"
                      aria-haspopup="menu"
                      aria-expanded={openMenu === workflow.id}
                      // Only referenced while the menu is mounted — a dangling
                      // aria-controls is worse than none.
                      aria-controls={openMenu === workflow.id ? `wf-row-menu-${workflow.id}` : undefined}
                      // detail === 0 means the click came from Enter/Space, not
                      // a pointer; that is the signal to focus the first item.
                      onClick={(e) => toggleMenu(workflow.id, e.currentTarget, e.detail === 0)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                    >
                      <DotsThree className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-border/50 dark:border-white/[0.06]">
                <p
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                >
                  Showing{' '}
                  <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
                    {rangeStart}–{rangeEnd}
                  </span>{' '}
                  of{' '}
                  <span className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontWeight: 510 }}>
                    {total}
                  </span>
                </p>
                <div className="flex items-center gap-3">
                  <span
                    className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={page === 0}
                      onClick={() => goToPage(page - 1)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                      aria-label="Previous page"
                    >
                      <CaretLeft className="h-4 w-4" weight="bold" />
                    </button>
                    <button
                      type="button"
                      disabled={page + 1 >= totalPages}
                      onClick={() => goToPage(page + 1)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                      aria-label="Next page"
                    >
                      <CaretRight className="h-4 w-4" weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Portal dropdown menu — escapes overflow-hidden of list Card */}
      {openMenu !== null && menuCoords && (() => {
        const workflow = workflows.find(w => w.id === openMenu)
        if (!workflow) return null
        return createPortal(
          <>
            <div className="fixed inset-0 z-[60]" aria-hidden="true" onClick={closeMenu} />
            <div
              ref={menuRef}
              id={`wf-row-menu-${workflow.id}`}
              role="menu"
              aria-label="Workflow actions"
              className={cn(
                'fixed w-52 rounded-lg py-1.5 z-[61]',
                'bg-card border border-border/60 shadow-lg',
                'dark:bg-[#191a1b] dark:border-white/[0.08]',
                'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
              )}
              style={{ top: menuCoords.top, right: menuCoords.right }}
            >
              {canActivate && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleToggleStatus(workflow)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-foreground hover:bg-accent/40 transition-colors duration-100 rounded-md mx-1 dark:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                >
                  {workflow.status === 'active' ? (
                    <><Pause className="w-3.5 h-3.5" /> Pause workflow</>
                  ) : (
                    <><Play className="w-3.5 h-3.5" /> Activate workflow</>
                  )}
                </button>
              )}
              {canWriteWorkflows && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleDuplicate(workflow.id)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-foreground hover:bg-accent/40 transition-colors duration-100 rounded-md mx-1 dark:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                >
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </button>
              )}
              {canWriteWorkflows && (
                <>
                  <div role="separator" className="border-t border-border/50 my-1 dark:border-white/[0.06]" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { closeMenu(); setDeleteConfirmId(workflow.id) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-red-600 hover:bg-red-50 transition-colors duration-100 rounded-md mx-1 dark:text-red-400 dark:hover:bg-red-500/[0.08]"
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px', width: 'calc(100% - 0.5rem)' }}
                  >
                    <Trash className="w-3.5 h-3.5" /> Delete
                  </button>
                </>
              )}
            </div>
          </>,
          document.body,
        )
      })()}

      {playgroundId && (
        <WorkflowPlaygroundModal
          isOpen
          onClose={() => { setPlaygroundId(null); setPlaygroundName('') }}
          workflowId={playgroundId}
          workflowName={playgroundName}
        />
      )}

      <AIWorkflowWizard
        isOpen={showAIWizard}
        onClose={() => setShowAIWizard(false)}
        onSuccess={(workflowId) => {
          setShowAIWizard(false)
          fetchWorkflows()
          fetchStats()
          // Open the builder as an unsaved draft — user saves to keep or discards to delete.
          navigate(`/workflows/builder/${workflowId}`, { state: { aiCreated: true } })
        }}
      />

      <ConfirmDialog
        open={deleteConfirmId !== null}
        title="Delete workflow"
        description="Are you sure you want to delete this workflow? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        loading={isDeleting}
        onConfirm={() => deleteConfirmId !== null && handleDelete(deleteConfirmId)}
        onCancel={() => !isDeleting && setDeleteConfirmId(null)}
      />
    </div>
  )
}

export default WorkflowsPage
